import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { DEFAULT_HL_GRADIENT } from '../_shared/headingHighlight.js';
/**
 * comparison-table — Shrine "Comparison table" at CONFIG-BY-CONFIG parity. A heading (with the full Shrine
 * word-HIGHLIGHT engine, BOTH engines, ALL sub-knobs) + body richtext + an optional CTA over a "us vs.
 * competitors" benefits table. The table is driven by `row` child blocks (one benefit per row), with a
 * checkmark/cross per column. Up to 3 competitor columns, a highlighted "us" column, three table styles
 * (classic / centered / minimal), a background-pattern engine, and per-icon styling — all wired in the
 * Component.
 *
 * PARITY APPROACH: rather than the shared `headingHighlightFields` helper (which only surfaces the style +
 * color + gradient and lets the engine default the rest), we declare the FULL Shrine highlight sub-fields
 * INLINE here — radius, border, padding, underline thickness/color/position, handwritten line color/stroke/
 * position — so every one of Shrine's settings has a real inspector control AND is read by the shared
 * `readHighlight` engine in the Component. Same for the highlighted/other color-scheme overlays (mapped to a
 * scheme-select + custom color overrides) and the check/cross icon styling.
 *
 * DEFERRED (Shopify-platform only — each with a reason; canonical list also in index.ts comment):
 *  - `display_id` — Shopify dev "copy section id" utility, no storefront meaning.
 *  - `atc_product` / `atc_button_label` / `atc_skip_cart` — Shopify AJAX add-to-cart on a marketing section;
 *    belongs to the product/cart app surface. The generic `buttonLabel`/`link` CTA covers "drive to product".
 *  - `custom_gradient_background` / `custom_image_background` / `custom_mobile_image_background` — Shopify
 *    image_picker + CDN responsive widths for the SECTION background; color comes from the universal scheme +
 *    the custom solid background color we DO implement. (The us/competitor LOGO image fields ARE implemented;
 *    only the CDN srcset machinery is dropped — a plain <img> is emitted.)
 *  - reveal / scroll entrance animations — deferred per section convention (no animation knobs).
 *  - Material-Symbols glyph font for the check/cross — replaced by an inline-SVG icon set.
 */
/** The 8-style word-highlight engine, declared INLINE with ALL Shrine sub-knobs (engine n = 1 | 2). */
function highlightFieldsFull(n) {
    const p = `headingHighlight${n}`;
    const G = 'General'; // Shrine groups the heading + both highlight engines under the "General" header.
    const isLine = {
        field: p,
        equals: ['line-1', 'line-2', 'line-3'],
    };
    const isUnderline = {
        field: p,
        equals: ['underline-solid', 'underline-gradient'],
    };
    const styleOptions = [
        { value: 'solid-color', label: 'Color' },
        { value: 'gradient-color', label: 'Gradient' },
        { value: 'bg-color', label: 'Background' },
        { value: 'underline-solid', label: 'Underline' },
        { value: 'underline-gradient', label: 'Gradient underline' },
        { value: 'line-1', label: 'Line 1' },
        { value: 'line-2', label: 'Line 2' },
        { value: 'line-3', label: 'Line 3' },
    ];
    return [
        {
            id: p,
            type: 'select',
            label: `Heading words highlight ${n}`,
            group: G,
            default: n === 1 ? 'solid-color' : 'none',
            info: n === 1
                ? 'Put words in BOLD to highlight them with this style.'
                : "Put words in ITALIC to highlight them with this style (keep it 'None' to use the default italic style).",
            options: n === 2
                ? [{ value: 'none', label: 'None' }, ...styleOptions]
                : [...styleOptions, { value: 'none', label: 'None' }],
        },
        {
            id: `${p}Gradient`,
            type: 'gradient',
            label: 'Highlight gradient',
            group: G,
            default: DEFAULT_HL_GRADIENT,
            visibleWhen: { field: p, equals: 'gradient-color' },
        },
        {
            id: `${p}BgColor`,
            type: 'color',
            label: 'Highlight background',
            group: G,
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}Color`,
            type: 'color',
            label: 'Highlight text color',
            group: G,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: p, equals: ['solid-color', 'bg-color'] },
        },
        {
            id: `${p}Radius`,
            type: 'range',
            label: 'Highlight radius',
            group: G,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 8,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}BorderWidth`,
            type: 'range',
            label: 'Highlight border width',
            group: G,
            min: 0,
            max: 6,
            step: 1,
            unit: 'px',
            default: 0,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}BorderStyle`,
            type: 'select',
            label: 'Highlight border style',
            group: G,
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}BorderColor`,
            type: 'color',
            label: 'Highlight border color',
            group: G,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}PaddingBlock`,
            type: 'range',
            label: 'Highlight vertical padding',
            group: G,
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.3,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}PaddingInline`,
            type: 'range',
            label: 'Highlight horizontal padding',
            group: G,
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.3,
            visibleWhen: { field: p, equals: 'bg-color' },
        },
        {
            id: `${p}UnderlineThickness`,
            type: 'range',
            label: 'Underline thickness',
            group: G,
            min: 0,
            max: 6,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: isUnderline,
        },
        {
            id: `${p}UnderlineColor`,
            type: 'color',
            label: 'Underline color',
            group: G,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: p, equals: 'underline-solid' },
        },
        {
            id: `${p}UnderlineGradient`,
            type: 'gradient',
            label: 'Underline gradient',
            group: G,
            default: DEFAULT_HL_GRADIENT,
            visibleWhen: { field: p, equals: 'underline-gradient' },
        },
        {
            id: `${p}UnderlinePosition`,
            type: 'range',
            label: 'Underline position',
            group: G,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 25,
            visibleWhen: isUnderline,
        },
        {
            id: `${p}LineColor`,
            type: 'color',
            label: 'Line color',
            group: G,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: isLine,
        },
        {
            id: `${p}LineWidth`,
            type: 'range',
            label: 'Line stroke width',
            group: G,
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 3,
            visibleWhen: isLine,
        },
        {
            id: `${p}LinePosition`,
            type: 'range',
            label: 'Line position',
            group: G,
            min: 0,
            max: 100,
            step: 5,
            default: 60,
            visibleWhen: isLine,
        },
    ];
}
/** The 6 Shrine color-scheme choices, mapped to our scheme-select + custom override (used by 2 overlays). */
const COLOR_SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
/**
 * One competitor column's config (Shrine "Competitor N" header): label + label font size + logo + desktop &
 * mobile logo widths — Shrine's exact labels. `numberOfCompetitors` controls how many columns RENDER, and
 * (unlike Shrine, which always shows all three groups) which of these groups the inspector offers.
 */
function competitorColumn(p, n) {
    const G = `Competitor ${n}`;
    // Competitor 1 is always drawn; 2 and 3 only exist while the table DISPLAYS that many columns (and never
    // under the Minimalistic style, which forces a single competitor). Shrine shows all three groups whatever
    // the count, and the founder filled "Competitor 2" then reported the second column missing (19/09/2026) —
    // a group of five controls that paint nothing is not parity worth keeping. Raising the count above brings
    // the group back with its stored values intact (`visibleWhen` gates rendering, never the document).
    const gate = n === 1
        ? undefined
        : [
            { field: 'numberOfCompetitors', equals: n === 2 ? [2, 3] : [3] },
            { field: 'style', notEquals: 'minimal' },
        ];
    return [
        {
            id: `${p}Label`,
            type: 'text',
            label: `Competitor ${n} label`,
            group: G,
            default: n === 1 ? 'Others' : `Concurrent ${n}`,
            maxLength: 40,
            info: 'Displayed if logo image is not selected.',
            visibleWhen: gate,
        },
        {
            id: `${p}LabelSize`,
            type: 'range',
            label: 'Label font size',
            group: G,
            min: 12,
            max: 28,
            step: 1,
            unit: 'px',
            default: 18,
            visibleWhen: gate,
        },
        { id: `${p}Logo`, type: 'image', label: `Competitor ${n} logo`, group: G, visibleWhen: gate },
        {
            id: `${p}LogoWidth`,
            type: 'range',
            label: 'Desktop logo width',
            group: G,
            min: 25,
            max: 150,
            step: 5,
            unit: 'px',
            default: 90,
            visibleWhen: gate,
        },
        {
            id: `${p}MobileLogoWidth`,
            type: 'range',
            label: 'Mobile logo width',
            group: G,
            min: 25,
            max: 100,
            step: 5,
            unit: 'px',
            default: 60,
            visibleWhen: gate,
        },
    ];
}
export const comparisonTableSchema = {
    type: 'comparison-table',
    name: 'Comparison table',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Visibility (Shrine `visibility`) ─────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            group: 'General',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
        // ── General: heading (RICHTEXT, first) → highlight 1 → highlight 2 → heading size (Shrine order) ──────
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: '<p>Tableau comparatif</p>' },
            info: 'Apply Bold (B) to a word to give it the highlight colour — like the Text block.',
        },
        ...highlightFieldsFull(1),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        // ── Body + CTA + alignment + pattern (Shrine "General") ──────────────────
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'General',
            default: {
                format: 'html',
                html: '<p>Expliquez pourquoi votre marque fait la différence face aux autres.</p>',
            },
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: 'General',
            default: 'Découvrir',
            defaultKey: 'hero.shop_now',
            maxLength: 60,
            info: 'Leave the label blank to hide the button.',
        },
        { id: 'link', type: 'link', label: 'Button link', group: 'General' },
        {
            id: 'buttonStyleSecondary',
            type: 'checkbox',
            label: 'Use outline button style',
            group: 'General',
            default: false,
        },
        // Add-to-Cart CTA (Shrine atc_*): a second button that adds a product to the cart.
        {
            id: 'atcButtonLabel',
            type: 'text',
            label: 'Add to Cart button label',
            group: 'General',
            maxLength: 60,
            info: 'Leave the label blank to hide the Add to Cart button.',
        },
        {
            id: 'atcProduct',
            type: 'product-picker',
            label: 'ATC Custom product',
            group: 'General',
            info: 'If empty, the button adds the main product from the product page to the cart (including the selected variant/quantity).',
        },
        {
            id: 'atcSkipCart',
            type: 'checkbox',
            label: 'ATC Custom product skip cart',
            group: 'General',
            default: false,
        },
        {
            id: 'desktopAlignment',
            type: 'select',
            label: 'Desktop content alignment',
            group: 'General',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'mobileContentAlignment',
            type: 'select',
            label: 'Mobile content alignment',
            group: 'General',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'customColorsBackground',
            type: 'color',
            label: 'Custom background',
            group: 'General',
            info: 'Custom color overrides apply when set to a non-default value (our universal scheme has no `custom` slot).',
            default: { ref: 'custom', value: '#FFFFFF' },
        },
        {
            id: 'customColorsText',
            type: 'color',
            label: 'Custom text',
            group: 'General',
            info: 'Overrides the section text/heading/body color when set to a non-default value.',
            default: { ref: 'custom', value: '#2E2A39' },
        },
        {
            id: 'customColorsSolidButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: 'General',
            auto: true,
            info: 'Automatic: the accent of your theme that is readable on this section\u2019s background.',
        },
        {
            id: 'customColorsSolidButtonText',
            type: 'color',
            label: 'Custom solid button text',
            group: 'General',
            auto: true,
            info: 'Automatic: the label colour that reads on the button fill.',
        },
        {
            id: 'customColorsOutlineButton',
            type: 'color',
            label: 'Custom outline button color',
            group: 'General',
            auto: true,
            info: 'Automatic: the text colour of this section\u2019s colour scheme.',
        },
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'General',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'General',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'General',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'General',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'General',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Table layout & style ─────────────────────────────────────────────────
        {
            id: 'desktopLayout',
            type: 'select',
            label: 'Desktop layout',
            group: 'Table',
            default: 'side-by-side',
            info: 'Side by side places the text column next to the table; Stacked puts it above (or below) the table.',
            options: [
                { value: 'side-by-side', label: 'Side by side' },
                { value: 'stacked', label: 'Stacked (vertical)' },
            ],
        },
        {
            id: 'layout',
            type: 'select',
            label: 'Desktop table placement',
            group: 'Table',
            default: 'table_second',
            info: 'In “Side by side” this is the table’s column (left/right); in “Stacked” it’s the table’s order (top/bottom).',
            options: [
                { value: 'table_first', label: 'Table first' },
                { value: 'table_second', label: 'Table second' },
            ],
        },
        {
            id: 'style',
            type: 'select',
            label: 'Table style',
            group: 'Table',
            default: 'classic',
            options: [
                { value: 'classic', label: 'Classic' },
                { value: 'centered', label: 'Centered' },
                { value: 'minimal', label: 'Minimal' },
            ],
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Table',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 20,
        },
        {
            id: 'benefitColumnWidth',
            type: 'range',
            label: 'Benefit column width',
            group: 'Table',
            min: 20,
            max: 70,
            step: 5,
            unit: '%',
            default: 50,
            info: 'Width of the left column where the benefit text is written — increase it to give the text more room.',
        },
        {
            id: 'cellTextSize',
            type: 'range',
            label: 'Cell text size',
            group: 'Table',
            min: 10,
            max: 40,
            step: 1,
            unit: 'px',
            default: 15,
            info: 'Size of the words written in a cell instead of an icon (e.g. "25 g", "Depends on the model").',
        },
        // ── Our label / logo ─────────────────────────────────────────────────────
        {
            id: 'usLabel',
            type: 'text',
            label: 'Our label',
            group: 'Our label/logo',
            default: 'Nous',
            maxLength: 40,
            info: 'Displayed if logo image is not selected.',
        },
        {
            id: 'usLabelSize',
            type: 'range',
            label: 'Label font size',
            group: 'Our label/logo',
            min: 12,
            max: 28,
            step: 1,
            unit: 'px',
            default: 18,
        },
        { id: 'logo', type: 'image', label: 'Our logo', group: 'Our label/logo' },
        {
            id: 'logoWidth',
            type: 'range',
            label: 'Desktop logo width',
            group: 'Our label/logo',
            min: 25,
            max: 150,
            step: 5,
            unit: 'px',
            default: 90,
        },
        {
            id: 'mobileLogoWidth',
            type: 'range',
            label: 'Mobile logo width',
            group: 'Our label/logo',
            min: 25,
            max: 100,
            step: 5,
            unit: 'px',
            default: 60,
        },
        // ── How many competitor columns ──────────────────────────────────────────
        // It used to sit in "Table", six controls away from everything that configures a competitor, and the
        // founder never found it: she filled in Competitor 2 and reported the column missing (19/09/2026). It
        // now opens the competitor part of the panel — the count first, then the columns it switches on.
        {
            id: 'numberOfCompetitors',
            type: 'range',
            label: 'Number of displayed competitors',
            group: 'Competitors',
            min: 1,
            max: 3,
            step: 1,
            default: 1,
            info: 'Each competitor you add gets its own settings below. Not available with the Minimalistic style.',
        },
        // ── Competitor 1 ─────────────────────────────────────────────────────────
        ...competitorColumn('others', 1),
        // ── Competitor 2 ─────────────────────────────────────────────────────────
        ...competitorColumn('others2', 2),
        // ── Competitor 3 ─────────────────────────────────────────────────────────
        ...competitorColumn('others3', 3),
        // ── Icons ────────────────────────────────────────────────────────────────
        {
            id: 'checkmarkStyle',
            type: 'select',
            label: 'Checkmark style',
            group: 'Icons',
            default: 'regular',
            options: [
                { value: 'regular', label: 'Regular' },
                { value: 'solid', label: 'Solid' },
            ],
            info: 'Solid is automatically selected if style is set to Minimalistic.',
        },
        {
            id: 'checkmarkColor',
            type: 'color',
            label: 'Checkmark color',
            group: 'Icons',
            auto: true,
            info: 'Automatic: green, adjusted to the cell it sits in so it reads on light and dark cells alike.',
        },
        {
            id: 'checkmarkBgColor',
            type: 'color',
            label: 'Solid checkmark background',
            group: 'Icons',
            auto: true,
            info: 'Automatic: the same green as the checkmark, with a white glyph on it.',
            visibleWhen: { field: 'checkmarkStyle', equals: 'solid' },
        },
        {
            id: 'xStyle',
            type: 'select',
            label: 'X style',
            group: 'Icons',
            default: 'regular',
            options: [
                { value: 'regular', label: 'Regular' },
                { value: 'solid', label: 'Solid' },
            ],
            info: 'Solid is automatically selected if style is set to Minimalistic.',
        },
        {
            id: 'xColor',
            type: 'color',
            label: 'X color',
            group: 'Icons',
            auto: true,
            info: 'Automatic: the cell\u2019s own text colour, faded \u2014 visible on any background.',
        },
        {
            id: 'xBgColor',
            type: 'color',
            label: 'Solid X background',
            group: 'Icons',
            auto: true,
            info: 'Automatic: a faint tint of the cell\u2019s own text colour.',
            visibleWhen: { field: 'xStyle', equals: 'solid' },
        },
        {
            id: 'oppositeIconColors',
            type: 'select',
            label: 'Opposite icons color',
            group: 'Icons',
            default: 'original',
            options: [
                { value: 'original', label: 'Original' },
                { value: 'inverse', label: 'Inverse' },
            ],
            info: 'Color of the checkmark when it\'s in "others" and X when it\'s in "us".',
        },
        // ── Classic & Centered style overlays ────────────────────────────────────
        {
            id: 'highlightedColorScheme',
            type: 'select',
            label: 'Highlighted cells color scheme',
            group: 'Classic & Centered style',
            default: 'accent-1',
            options: COLOR_SCHEME_OPTIONS,
        },
        {
            id: 'customColorsHighlightedBackground',
            type: 'color',
            label: 'Highlighted background',
            group: 'Classic & Centered style',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'highlightedColorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsHighlightedText',
            type: 'color',
            label: 'Highlighted text',
            group: 'Classic & Centered style',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'highlightedColorScheme', equals: 'custom' },
        },
        {
            id: 'highlightedSeparatorOpacity',
            type: 'range',
            label: 'Highlighted cells separator opacity',
            group: 'Classic & Centered style',
            min: 0,
            max: 40,
            step: 2,
            unit: '%',
            default: 0,
        },
        {
            id: 'highlightedOverlayOpacity',
            type: 'range',
            label: 'Even rows highlighted cells overlay opacity',
            group: 'Classic & Centered style',
            min: 0,
            max: 20,
            step: 1,
            unit: '%',
            default: 0,
        },
        {
            id: 'otherCellsColorScheme',
            type: 'select',
            label: 'Regular cells color scheme',
            group: 'Classic & Centered style',
            default: 'background-1',
            options: COLOR_SCHEME_OPTIONS,
        },
        {
            id: 'customColorsOthersBackground',
            type: 'color',
            label: 'Other cells background',
            group: 'Classic & Centered style',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'otherCellsColorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsOthersText',
            type: 'color',
            label: 'Other cells text',
            group: 'Classic & Centered style',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'otherCellsColorScheme', equals: 'custom' },
        },
        {
            id: 'regularSeparatorOpacity',
            type: 'range',
            label: 'Regular cells separator opacity',
            group: 'Classic & Centered style',
            min: 0,
            max: 40,
            step: 2,
            unit: '%',
            default: 10,
        },
        {
            id: 'regularOverlayOpacity',
            type: 'range',
            label: 'Even rows regular cells overlay opacity',
            group: 'Classic & Centered style',
            min: 0,
            max: 20,
            step: 1,
            unit: '%',
            default: 0,
        },
        // ── Minimalistic style ───────────────────────────────────────────────────
        {
            id: 'minimalisticBorderOpacity',
            type: 'range',
            label: 'Rows separator opacity',
            group: 'Minimalistic style',
            min: 0,
            max: 40,
            step: 2,
            unit: '%',
            default: 16,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: color scheme + padding + width) ──────────────────
        ...universalFields,
    ],
    // Each benefit ROW is an inline child block (Shrine `row`): label + per-column checkmark toggles.
    blocks: [
        {
            type: 'row',
            name: 'Benefit',
            max: 24,
            fields: [
                // Richtext (was plain text): bold/italic/links in the benefit label. Older rows still carry a plain
                // STRING — richHeading() renders both, the inspector control tolerates both.
                {
                    id: 'benefit',
                    type: 'richtext',
                    label: 'Benefit',
                    default: { format: 'html', html: '<p>Avantage</p>' },
                },
                // Every column offers a TICK (checkbox) and, beside it, a free TEXT. A filled text REPLACES the
                // icon in that cell — "25 g", "7 to 15 days", "Depends on the model", "No*". The founder needed to
                // rebuild a Shrine table where half the cells are words (21/09/2026); a tick-only row could not say
                // "Depends on the model". Empty text = the icon, so every saved table renders exactly as before.
                { id: 'us', type: 'checkbox', label: 'Us', default: true },
                {
                    id: 'usText',
                    type: 'text',
                    label: 'Us — text instead of the icon',
                    placeholder: 'e.g. 25 g',
                    info: 'Leave empty to show the checkmark or the X.',
                },
                { id: 'others', type: 'checkbox', label: 'Competitor 1', default: false },
                {
                    id: 'othersText',
                    type: 'text',
                    label: 'Competitor 1 — text instead of the icon',
                    placeholder: 'e.g. Depends on the model',
                    info: 'Leave empty to show the checkmark or the X.',
                },
                // A tick per DISPLAYED column. `scope: 'section'` reads the SECTION's count, not the row's own
                // settings: a row offering "Competitor 2" while the table draws one competitor is a switch that
                // paints nothing, and it is the switch the founder used when she reported her second competitor
                // missing (19/09/2026). The stored value survives — lower the count, raise it back, the ticks are
                // where she left them.
                {
                    id: 'others2',
                    type: 'checkbox',
                    label: 'Competitor 2',
                    default: false,
                    visibleWhen: [
                        { field: 'numberOfCompetitors', equals: [2, 3], scope: 'section' },
                        { field: 'style', notEquals: 'minimal', scope: 'section' },
                    ],
                },
                {
                    id: 'others2Text',
                    type: 'text',
                    label: 'Competitor 2 — text instead of the icon',
                    placeholder: 'e.g. Depends on the model',
                    info: 'Leave empty to show the checkmark or the X.',
                    visibleWhen: [
                        { field: 'numberOfCompetitors', equals: [2, 3], scope: 'section' },
                        { field: 'style', notEquals: 'minimal', scope: 'section' },
                    ],
                },
                {
                    id: 'others3',
                    type: 'checkbox',
                    label: 'Competitor 3',
                    default: false,
                    visibleWhen: [
                        { field: 'numberOfCompetitors', equals: [3], scope: 'section' },
                        { field: 'style', notEquals: 'minimal', scope: 'section' },
                    ],
                },
                {
                    id: 'others3Text',
                    type: 'text',
                    label: 'Competitor 3 — text instead of the icon',
                    placeholder: 'e.g. Depends on the model',
                    info: 'Leave empty to show the checkmark or the X.',
                    visibleWhen: [
                        { field: 'numberOfCompetitors', equals: [3], scope: 'section' },
                        { field: 'style', notEquals: 'minimal', scope: 'section' },
                    ],
                },
            ],
        },
    ],
    maxBlocks: 24,
};
export const comparisonTablePresets = [
    {
        name: 'Comparison table',
        settings: {
            visibility: 'always-display',
            // `title` is a RICHTEXT field: richHeading() ESCAPES a bare string (legacy `**bold**` markup), so a
            // raw-HTML sentence handed over as a string renders `<strong>` literally in the heading AND in the
            // section-picker preview. The marker therefore goes inside the {format, html} shape — applyDefaults
            // resolves `@t:` one level down too.
            title: { format: 'html', html: '@t:section.why_us_html' },
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            text: {
                format: 'html',
                html: '<p>Voyez en un coup d’œil pourquoi notre produit fait la différence face à la concurrence.</p>',
            },
            buttonLabel: '@t:hero.shop_now',
            link: { type: 'page', slug: 'products', label: 'Découvrir' },
            buttonStyleSecondary: false,
            desktopAlignment: 'center',
            mobileContentAlignment: 'center',
            customColorsText: { ref: 'custom', value: '#2E2A39' },
            patternBg: 'none',
            layout: 'table_second',
            style: 'centered',
            cornerRadius: 20,
            numberOfCompetitors: 1,
            usLabel: '@t:product.us_column',
            usLabelSize: 18,
            logoWidth: 90,
            mobileLogoWidth: 60,
            othersLabel: '@t:section.others',
            othersLabelSize: 18,
            othersLogoWidth: 90,
            othersMobileLogoWidth: 60,
            checkmarkStyle: 'regular',
            xStyle: 'regular',
            oppositeIconColors: 'original',
            highlightedColorScheme: 'accent-1',
            otherCellsColorScheme: 'background-1',
            regularSeparatorOpacity: 10,
            minimalisticBorderOpacity: 16,
            colorScheme: 'light',
            paddingTop: 72,
            paddingBottom: 72,
            width: 'standard',
        },
        blocks: [
            {
                type: 'row',
                settings: {
                    benefit: { format: 'html', html: '<p>Livraison gratuite</p>' },
                    us: true,
                    others: false,
                },
            },
            {
                type: 'row',
                settings: {
                    benefit: { format: 'html', html: '@t:checkout.pay_on_delivery' },
                    us: true,
                    others: false,
                },
            },
            {
                type: 'row',
                settings: {
                    benefit: { format: 'html', html: '<p>Retours sous 30 jours</p>' },
                    us: true,
                    others: true,
                },
            },
            {
                type: 'row',
                settings: {
                    benefit: { format: 'html', html: '<p>Service client 7j/7</p>' },
                    us: true,
                    others: false,
                },
            },
            {
                type: 'row',
                settings: {
                    benefit: { format: 'html', html: '@t:section.quality_guaranteed' },
                    us: true,
                    others: true,
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const comparisonTableMeta = {
    label: 'Comparison table',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map